import React from "react";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import ToolTip, { TooltipElementType } from "@swiggy-private/dls-web-core/dist/components/Tooltip";

import {
    Container,
    Header,
    HeaderImage,
    HeaderText,
    Body,
    InfoRow,
    InfoRowMain,
    IconWell,
    RowText,
    RowDivider,
    Cta,
    TooltipInteract,
    TooltipOverlay,
    DeliveryTypeRowWrap,
} from "./DeliveryDetailsWidget.style";
import { DELIVERY_DETAILS_IMAGE_SIZE } from "@cart-ui/constants";
import { IHeaderData, IDeliveryRowData, IToolTipData } from "@cart-ui/interfaces";

export interface DeliveryDetailsWidgetProps {
    headerData: IHeaderData;
    deliveryContentData: IDeliveryRowData;
    deliveryContactData: IDeliveryRowData;
    toolTipData: IToolTipData;
    hideEditCta?: boolean;
    onLoadImpression?: () => void;
}

const DeliveryDetailsRow: React.FC<{ rowData: IDeliveryRowData; hideEditCta?: boolean }> = ({
    rowData,
    hideEditCta,
}) => (
    <InfoRow>
        <InfoRowMain>
            <IconWell aria-hidden $iconBackground={rowData.iconBackground}>
                {rowData.icon}
            </IconWell>
            <RowText>
                <Text variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                    {rowData.title}
                </Text>
                {rowData.subtitle ? (
                    <Text variant="V2.body2.neutral.medium" color="text_High_Emphasis">
                        {rowData.subtitle}
                    </Text>
                ) : null}
            </RowText>
        </InfoRowMain>
        {hideEditCta ? null : (
            <Cta
                onClick={rowData.ctaAction}
                variant="V2.body2.neutral.bold"
                color="primary"
                role="button"
                data-testid={rowData.ctaTestId}
            >
                {rowData.ctaLabel}
            </Cta>
        )}
    </InfoRow>
);

const DeliveryDetailsWidget: React.FC<DeliveryDetailsWidgetProps> = ({
    headerData,
    deliveryContentData,
    deliveryContactData,
    toolTipData,
    hideEditCta,
    onLoadImpression,
}) => {
    useEffectOnce(() => {
        onLoadImpression?.();
    });

    return (
        <Container>
            <Header data-testid="on-premise-fulfilment-widget-header" $background={headerData.background}>
                <HeaderText>
                    <Text variant="V2.body2.neutral.bold" color="text_High_Emphasis">
                        {headerData.title}
                    </Text>
                    <Text variant="V2.overline.neutral.medium" color="text_Med_Emphasis">
                        {headerData.subtitle}
                    </Text>
                </HeaderText>
                <HeaderImage>
                    <Image
                        width={DELIVERY_DETAILS_IMAGE_SIZE.HEADER_IMAGE_SIZE}
                        height={DELIVERY_DETAILS_IMAGE_SIZE.HEADER_IMAGE_SIZE}
                        cloudinaryId={headerData.imageUrl}
                    />
                </HeaderImage>
            </Header>
            <Body>
                <DeliveryTypeRowWrap>
                    <DeliveryDetailsRow rowData={deliveryContentData} hideEditCta={hideEditCta} />
                    {toolTipData.text ? (
                        <TooltipOverlay aria-live="polite">
                            <TooltipInteract>
                                <ToolTip
                                    direction="up-left"
                                    text={toolTipData.text}
                                    elementType={TooltipElementType.LEFT_ASSET_16X16}
                                    onClose={toolTipData.onDismiss}
                                    isFullWidth={false}
                                    leftAsset={toolTipData.leftAsset}
                                />
                            </TooltipInteract>
                        </TooltipOverlay>
                    ) : null}
                </DeliveryTypeRowWrap>
                <RowDivider />
                <DeliveryDetailsRow rowData={deliveryContactData} hideEditCta={hideEditCta} />
            </Body>
        </Container>
    );
};

export default React.memo(DeliveryDetailsWidget);
